'use client';

import { useEffect, useId, useRef, useState } from 'react';
import type { RedirectForm } from '@/lib/payment/types';

interface GatewayRedirectFormProps {
  redirect: RedirectForm;
  /** Short status line shown while the browser navigates to the gateway. */
  message: string;
  /** Label of the manual submit button ("Continue to payment"). */
  continueLabel: string;
  /** Label of the button that abandons checkout ("Cancel"). */
  cancelLabel: string;
  /** Called when the buyer backs out; the caller clears its payment state. */
  onCancel?: () => void;
}

/**
 * Hands the buyer to the gateway's hosted payment page with a plain browser
 * form POST (WayForPay /pay, LiqPay /api/3/checkout). A top-level navigation
 * is the whole point: Apple Pay only renders on the gateway's own verified
 * domain, never inside the on-page widget iframe.
 *
 * The auto-submit is a convenience, never the only way out. Two things break
 * it: a browser that blocks the programmatic submit, and the Back button –
 * a bfcache restore replays no effects, so this overlay comes back exactly
 * as it was, spinner and all, with nothing left to submit it. Both leave the
 * buyer staring at a dead spinner unless there is a visible control, so the
 * continue and cancel buttons are always rendered, and a `pageshow` with
 * `persisted` drops the "redirecting" framing in favour of them.
 */
export default function GatewayRedirectForm({
  redirect,
  message,
  continueLabel,
  cancelLabel,
  onCancel,
}: GatewayRedirectFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const submittedRef = useRef(false);
  const formId = useId();
  const [restored, setRestored] = useState(false);

  useEffect(() => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    formRef.current?.submit();
  }, []);

  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) setRestored(true);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 max-w-sm w-full text-center">
        {/* Only the status line is a live region - with role/aria-live on the
            overlay itself, the two buttons below were announced along with
            every change to it. */}
        {!restored && (
          <div className="flex items-center justify-center gap-3" role="status" aria-live="polite">
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" aria-hidden="true" />
            <span className="text-sm text-gray-700">{message}</span>
          </div>
        )}
        <div className={`${restored ? '' : 'mt-5 '}flex flex-col gap-2`}>
          <button
            type="submit"
            form={formId}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
          >
            {continueLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition-colors"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
      <form ref={formRef} id={formId} action={redirect.action} method={redirect.method} className="hidden" aria-hidden="true">
        {redirect.fields.map((f, i) => (
          <input key={`${f.name}-${i}`} type="hidden" name={f.name} value={f.value} readOnly />
        ))}
      </form>
    </div>
  );
}
